import { useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import type {
  GetSeriesBehindTheScenesQuery,
  GetSeriesBehindTheScenesQueryVariables,
} from "@/codegen/graphql/generated/graphql";
import { GET_SERIES_BEHIND_THE_SCENES } from "@/codegen/graphql/queries/GetSeriesBehindTheScenes.query";
import usePremium from "@/hooks/usePremium";

export type SeriesBts =
  GetSeriesBehindTheScenesQuery["show"]["behindTheScenes"][number];

// A VIP still reading `locked` is the server lagging behind the client — a
// purchase whose webhook hasn't landed, a status refreshed on login — so the
// query is asked again, a few times, before giving up (~30 s in all).
const LOCKED_RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000];

/**
 * A series' Behind the Scenes (SHO-757). `locked` and `url` are the user's:
 * a VIP gets the playlists, a free user `locked: true` and no `url`. Fetched
 * from a component — the route loader runs before boot holds a token — and
 * again whenever the premium status flips, so a purchase or a login made on
 * the page unlocks the videos without a reload. The client is ahead of the
 * server then (`grantPremiumAccess` is the grace period before Stripe's
 * webhook), so a VIP whose videos come back locked retries on a backoff;
 * BTS is the first content the web locks server-side, there is no
 * client-side fallback. `undefined` until answered, and with no `showId`.
 */
export default function useBehindTheScenes(showId: string) {
  const { isPremiumUser } = usePremium();
  const { data, refetch } = useQuery<
    GetSeriesBehindTheScenesQuery,
    GetSeriesBehindTheScenesQueryVariables
  >(GET_SERIES_BEHIND_THE_SCENES, {
    variables: { showId },
    // The View all page asks before the series is in (a language switch
    // resets the store): `show(id: "")` is an `Invalid show ID` the
    // ErrorLink would report.
    skip: !showId,
  });
  const videos = data?.show.behindTheScenes;

  // On a real flip only: the effect also runs on every mount, where the
  // cache already answers (rail → View all → back).
  const knownPremium = useRef(isPremiumUser);
  useEffect(() => {
    if (knownPremium.current === isPremiumUser) return;
    knownPremium.current = isPremiumUser;
    void refetch();
  }, [isPremiumUser, refetch]);

  // Driven by a counter, not by `data`: a refetch answering the same thing
  // leaves `data` referentially equal, and the next retry would never fire.
  const [retry, setRetry] = useState(0);
  const stillLocked =
    isPremiumUser && (videos?.some((bts) => bts.locked) ?? false);
  useEffect(() => {
    if (!stillLocked) {
      setRetry(0);
      return;
    }
    if (retry >= LOCKED_RETRY_DELAYS_MS.length) return;
    const timeout = setTimeout(() => {
      void refetch().finally(() => setRetry((count) => count + 1));
    }, LOCKED_RETRY_DELAYS_MS[retry]);
    return () => clearTimeout(timeout);
  }, [stillLocked, retry, refetch]);

  return videos;
}
